/**
 * Check Valve — allows forward flow only, blocks reverse.
 *
 * N2 FIX (hydraulic honesty). The old implementation put K in metadata that
 * nothing consumed — a "closed" check valve (K = 1e6) blocked NOTHING.
 * The valve now acts on the network through two live channels:
 *
 *   OPEN   → flow-dependent resistance ΔP = −K_open·ρ·u·|u|/2 (sign-aware,
 *            via the shared valveDeltaP helper).
 *   CLOSED → hard mass-flow constraint `mdot: 0` on the outlet face — the
 *            network enforces it every SIMPLE iteration, so reverse delivery
 *            through the valve is EXACTLY zero (an ideal check valve), not
 *            merely resisted.
 *
 * OPEN/CLOSED decision — HYSTERETIC LATCH on the live path (a real disc):
 *   • CLOSED → reopens ONLY on the pressure criterion: across-valve
 *     ΔP = P_up − P_down > crackingPressure (nsNetwork propagates the
 *     adjacent boundary-cell pressures as _P_up/_P_down, because the live
 *     component sits on a SINGLE junction where inlet === outlet and the
 *     junction ΔP is identically 0).
 *   • OPEN → closes ONLY on clear reverse throughflow (_throughMdot < −ε).
 *     Established forward flow holds the disc open even when the running
 *     resistive drop is below the cracking threshold, exactly like hardware.
 *   The latch makes the closed state immune to the small face-flow noise the
 *   pressure-correction step leaves on pinned faces between re-pins (a
 *   stateless flow-sign test flip-flopped open on that noise).
 *   • Standalone/unit-test path (distinct inlet/outlet junctions, no solver
 *     context): the original stateless junction-ΔP criterion is preserved.
 *   • No context at all on a shared junction: defaults OPEN (passthrough) —
 *     matches the legacy live behaviour and cannot deadlock a starting loop.
 *
 * The latch is PRIVATE INSTANCE STATE mutated across steps — registered in
 * worker/serialization.ts PRIVATE_FIELDS (like NSReliefValve.isOpen) so a
 * worker round-trip cannot silently reset it (CLAUDE.md stale-state rule).
 */
import type { NSComponent, Junction, ComponentUpdate } from '../types';
import { valveDeltaP } from './nsValve';

/** ṁ below −FLOW_EPS is a clear reverse flow (closes an open disc). */
const FLOW_EPS = 1e-6; // kg/s

export class NSCheckValve implements NSComponent {
  id: string;
  type = 'checkValve';
  params: Record<string, any>;
  /** Hysteretic disc state (live path only). undefined = not yet initialised. */
  private isOpenLatch: boolean | undefined = undefined;

  constructor(id: string, params: {
    K_open?: number;       // Loss coefficient when open (default 2.5)
    K_closed?: number;     // Loss coefficient when closed (default 1e6, informational)
    crackingPressure?: number; // Pa — minimum ΔP to open (default 1000)
  }) {
    this.id = id;
    this.params = { K_open: 2.5, K_closed: 1e6, crackingPressure: 1000, ...params };
  }

  applyBoundaryConditions(inlet: Junction, outlet: Junction, _dt: number): ComponentUpdate {
    const { K_open, K_closed, crackingPressure } = this.params;

    // Across-valve pressure differential. Prefer the solver-propagated
    // adjacent-cell pressures (live network: inlet === outlet junction);
    // fall back to the distinct-junction difference (standalone/unit tests).
    const P_up = this.params._P_up;
    const P_down = this.params._P_down;
    const hasCellDP = typeof P_up === 'number' && typeof P_down === 'number' &&
      isFinite(P_up) && isFinite(P_down);
    const dP = hasCellDP ? P_up - P_down : inlet.P - outlet.P;

    // Signed throughflow from the solver (undefined on the standalone path).
    const mdotThrough = typeof this.params._throughMdot === 'number' &&
      isFinite(this.params._throughMdot)
      ? this.params._throughMdot
      : null;

    let isOpen: boolean;
    if (mdotThrough !== null && hasCellDP) {
      // LIVE path — hysteretic disc latch.
      if (this.isOpenLatch === undefined) {
        this.isOpenLatch = dP > crackingPressure; // initial disc position
      }
      if (this.isOpenLatch) {
        if (mdotThrough < -FLOW_EPS) this.isOpenLatch = false; // reverse → slam shut
      } else {
        if (dP > crackingPressure) this.isOpenLatch = true;    // pressure reopen
      }
      isOpen = this.isOpenLatch;
    } else if (mdotThrough !== null) {
      // Partial context (flow but no cell pressures): flow-sign only;
      // stagnant defaults open so a quiescent startup cannot deadlock.
      isOpen = mdotThrough >= -FLOW_EPS;
    } else if (inlet !== outlet || hasCellDP) {
      // Standalone path (distinct junctions): original cracking criterion.
      isOpen = dP > crackingPressure;
    } else {
      // Shared junction with no context — legacy passthrough.
      isOpen = true;
    }

    const K = isOpen ? K_open : K_closed;

    if (!isOpen) {
      return {
        deltaP: 0,
        deltaH: 0, // Isenthalpic
        mdot: 0,   // HARD block: network zeroes the outlet face every iteration
        momentumSource: 0,
        energySource: 0,
        metadata: { K, isOpen: 0, dP },
      };
    }

    // Open: quadratic K_open resistance from the live throughflow (0 without
    // solver context — standalone behaviour unchanged). inlet.P feeds the
    // rail-guard cap (|ΔP| ≤ LOSS_CAP_FRACTION·P_inlet).
    const deltaP = valveDeltaP(this.params, K_open, undefined, inlet?.P);

    return {
      deltaP,
      deltaH: 0, // Isenthalpic
      momentumSource: 0,
      energySource: 0,
      metadata: { K, isOpen: 1, dP, deltaP_valve: deltaP },
    };
  }
}
